import { normalizeRoleSlug } from "./roleAccess";
import {
  clearAuthStorage,
  getAuthItem,
  setAuthItem,
} from "./authStorage";

/** Post-login landing paths for visitor workspace roles. */
export const VISITOR_ROLE_LANDING_PATHS = {
  admin: "/app/visitors/control",
  office: "/app/visitors",
  receptionist: "/app/visitors",
};

/**
 * Resolve role string from login API body (user or admin auth).
 * Matches: { role, user: { role } } from POST /api/user/auth/login.
 */
export function resolveRoleFromLoginResponse(data) {
  return (
    data?.role ||
    data?.role_name ||
    data?.user?.role ||
    data?.user?.role_name ||
    data?.admin?.role ||
    data?.admin?.role_name ||
    ""
  );
}

/**
 * Persist JWT session + user snapshot for sidebar/profile.
 * Does not replace auth context — uses existing localStorage contract.
 */
export function persistUserSession({ token, csrfToken, data }) {
  if (typeof window === "undefined") return;

  if (token) {
    setAuthItem("jwt_token", token);
  }

  if (csrfToken) {
    setAuthItem("csrf_token", csrfToken);
    setAuthItem("csrf-token", csrfToken);
  }

  const roleRaw = resolveRoleFromLoginResponse(data);
  const roleSlug = normalizeRoleSlug(roleRaw);
  if (roleSlug) {
    setAuthItem("auth_role", roleSlug);
  }

  const user = data?.user ?? data?.admin ?? null;
  if (user || roleRaw) {
    setAuthItem(
      "auth_user",
      JSON.stringify({
        id: user?.id ?? user?.tbl_user_id ?? user?.studentId ?? user?.donorId ?? null,
        name:
          user?.name ??
          user?.full_name ??
          user?.admin_name ??
          user?.donor_name ??
          "",
        email: user?.email ?? "",
        role: roleSlug || normalizeRoleSlug(user?.role),
      }),
    );
  }
}

/** Clear auth keys (extends httpClient session clear). */
export function clearAuthSession() {
  clearAuthStorage();
}

export function getStoredAuthUser() {
  if (typeof window === "undefined") return null;
  try {
    const raw = getAuthItem("auth_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getStoredAuthRole() {
  if (typeof window === "undefined") return "";
  return (
    getAuthItem("auth_role") ||
    normalizeRoleSlug(getStoredAuthUser()?.role) ||
    ""
  );
}

/**
 * Redirect target after successful login (JWT role slug).
 * Visitor roles land on /app visitor screens; others keep role-shell dashboard.
 */
export function getPostLoginRedirectPath(role) {
  const slug = normalizeRoleSlug(role);
  if (VISITOR_ROLE_LANDING_PATHS[slug]) {
    return VISITOR_ROLE_LANDING_PATHS[slug];
  }
  if (!slug) return "/user-login";
  return `/${slug}/dashboard`;
}

/** Default “home” link for locked-screen guard / back navigation. */
export function getRoleHomePath(roleSlug) {
  return getPostLoginRedirectPath(roleSlug);
}
